import React from 'react';
import { useTranslation } from 'react-i18next';
import { ReactTinyLink } from 'react-tiny-link';
import { XMarkIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';

function LinkPreview({ url, title, imageURL, onDelete, clickable = true }) {
  const { t } = useTranslation();
  // If we have the image and title (cached from iOS/backend), show custom compact view matching iOS
  if (title && imageURL) {
    return (
      <div className="relative group">
        <div 
          className={`flex items-center gap-3 p-3 bg-gray-100/50 rounded-xl border border-gray-200/50 transition-colors ${clickable ? 'hover:bg-gray-100 cursor-pointer' : ''}`}
          onClick={(e) => {
            if (clickable) {
              e.stopPropagation();
              window.open(url, '_blank', 'noopener,noreferrer');
            }
          }}
        >
          {/* Image - 66x66 to match iOS large mode approx, or 55x55 */}
          <div className="flex-shrink-0">
            <img 
              src={imageURL} 
              alt={title} 
              className="w-[55px] h-[55px] md:w-[66px] md:h-[66px] rounded-lg object-cover bg-gray-200"
              onError={(e) => {
                e.target.onerror = null; 
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            {/* Fallback if image errors */}
            <div className="hidden w-[55px] h-[55px] md:w-[66px] md:h-[66px] rounded-lg bg-gray-200 items-center justify-center">
              <ArrowTopRightOnSquareIcon className="w-6 h-6 text-gray-400" />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-gray-900 line-clamp-2 leading-tight mb-0.5">
              {title}
            </h4>
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <span className="truncate text-[#F07807] font-medium">{url}</span>
              {clickable && <ArrowTopRightOnSquareIcon className="w-3 h-3 text-[#F07807]" />}
            </div>
          </div>
        </div>

        {/* Delete Button Overlay */}
        {onDelete && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="absolute -top-2 -right-2 bg-white text-red-500 rounded-full p-1 shadow-sm border border-gray-200 opacity-0 group-hover:opacity-100 transition-opacity"
            title={t('common.delete')}
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  }

  // Fallback to ReactTinyLink if we don't have metadata (will fetch client-side)
  return (
    <div className="relative group">
      <div className="notemail-link-preview">
        <ReactTinyLink
          cardSize="small"
          showGraphic={true}
          maxLine={2}
          minLine={1}
          url={url}
          header={title} // Pass title if we have it but no image
          defaultMedia={imageURL} // Pass image if we have it but no title?
          width="100%"
          activeColor="#F07807"
          proxyUrl={process.env.REACT_APP_CORS_PROXY} // Use proxy if available in env, otherwise default
        />
      </div>
      
      {onDelete && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="absolute -top-2 -right-2 z-10 bg-white text-red-500 rounded-full p-1 shadow-sm border border-gray-200 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

export default LinkPreview;

